"use client";
import React, { useRef } from "react";
import { useScroll, useTransform, motion, MotionValue } from "framer-motion";

export const ContainerScroll = ({
  titleComponent,
  children,
}: {
  titleComponent: string | React.ReactNode;
  children: React.ReactNode;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
  });
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => {
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  const scaleDimensions = () => {
    return isMobile ? [0.7, 0.9] : [1.05, 1];
  };

  const rotate = useTransform(scrollYProgress, [0, 1], [20, 0]);
  const scale = useTransform(scrollYProgress, [0, 1], scaleDimensions());
  const translate = useTransform(scrollYProgress, [0, 1], [0, -100]);

  return (
    <div
      className="min-h-[45rem] md:h-[55rem] flex items-center justify-center relative p-2 md:p-20"
      ref={containerRef}
    >
      <div
        className="py-2 md:py-6 w-full relative"
        style={{
          perspective: "1000px",
        }}
      >
        <Header translate={translate} titleComponent={titleComponent} />
        <Card rotate={rotate} translate={translate} scale={scale}>
          {children}
        </Card>
      </div>
    </div>
  );
};

export const Header = ({ translate, titleComponent }: any) => {
  return (
    <motion.div
      style={{
        translateY: translate,
      }}
      className="div max-w-5xl mx-auto text-center"
    >
      {titleComponent}
    </motion.div>
  );
};

export const Card = ({
  rotate,
  scale,
  translate,
  children,
}: {
  rotate: MotionValue<number>;
  scale: MotionValue<number>;
  translate: MotionValue<number>;
  children: React.ReactNode;
}) => {
  // Glare and edge lighting are strongest while the device is tilted, and settle
  // as it rotates flat — the cue that sells the depth.
  const glare = useTransform(rotate, [20, 0], [0.5, 0.12]);
  const edgeLight = useTransform(rotate, [20, 0], [0.9, 0.35]);

  return (
    <motion.div
      style={{
        rotateX: rotate,
        scale,
        transformStyle: "preserve-3d",
        boxShadow:
          "0 0 #0000004d, 0 9px 20px #0000004a, 0 37px 37px #00000042, 0 84px 50px #00000026, 0 149px 60px #0000000a, 0 233px 65px #00000003",
      }}
      className="relative max-w-5xl -mt-12 md:-mt-6 mx-auto min-h-[35rem] md:h-[40rem] w-full border-4 border-[#6C6C6C] p-2 md:p-6 bg-gradient-to-b from-[#2f2f2f] via-[#222222] to-[#151515] rounded-[30px] shadow-2xl"
    >
      {/* Chassis extrusion — layers sitting behind the face, scaled up just enough
          to peek out around the silhouette and read as the device's thickness. */}
      {[
        { depth: 8, up: 1.006, tone: "#1a1a1a" },
        { depth: 16, up: 1.012, tone: "#111111" },
        { depth: 24, up: 1.018, tone: "#0a0a0a" },
      ].map(({ depth, up, tone }) => (
        <div
          key={depth}
          aria-hidden
          className="pointer-events-none absolute inset-0 rounded-[32px]"
          style={{
            transform: `translateZ(-${depth}px) scale(${up})`,
            backgroundColor: tone,
          }}
        />
      ))}

      {/* Bevel — light catching the top edge, shadow pooling at the bottom */}
      <motion.div
        aria-hidden
        style={{ opacity: edgeLight }}
        className="pointer-events-none absolute inset-0 rounded-[30px] bg-gradient-to-b from-white/25 via-transparent to-black/40"
      />

      {/* Screen, recessed under the bezel */}
      <div
        className="relative h-full w-full overflow-y-auto md:overflow-hidden rounded-2xl bg-gray-100 dark:bg-zinc-900 md:rounded-2xl md:p-4 shadow-[inset_0_2px_12px_rgba(0,0,0,0.45)]"
        style={{ transform: "translateZ(4px)" }}
      >
        {children}
      </div>

      {/* Glass glare across the screen */}
      <motion.div
        aria-hidden
        style={{ opacity: glare, transform: "translateZ(6px)" }}
        className="pointer-events-none absolute inset-2 md:inset-6 rounded-2xl bg-gradient-to-tr from-white/40 via-transparent to-transparent"
      />
    </motion.div>
  );
};